/**
 * MONSTER TOWN — COOKING DATA MODEL. OWNED BY THE COOKING CREW (src/cooking/**).
 *
 * THE WHOLE POINT OF THIS FILE: a new recipe must be a DATA DROP into
 * `src/content/recipes.ts` and nothing else. The kitchen in `sheet.ts` reads
 * only the fields below; it has no idea what a fruit bowl is.
 *
 * WHAT COOKING IS, MECHANICALLY (expansion brief §5):
 *   sequencing  — the ingredients go in IN ORDER, and the card shows the order
 *                 as pictures.
 *   counting    — each step wants N of a thing, drawn as N countable dots.
 *   a satisfying tap — the stir, three times, and out comes a dish.
 *
 * WHAT COOKING IS NOT:
 *   - NOTHING BURNS. There is no heat, no timer, no doneness and no field here
 *     you could add one to. `RecipeDef` has no `time`, no `difficulty`.
 *   - NOTHING FAILS. A wrong ingredient is a giggle and a swap — the kitchen
 *     never consumes it, never scolds, and never ends the recipe. The only exit
 *     from cooking is the back button or a finished dish.
 *   - NO READING. `devName` is dev-only (LAW #4). Every card is `pic` + colour.
 */

import type { PaletteName } from '../core/palette'
import type { SourcePic } from './pics'

// ---------------------------------------------------------------------------
// pictures — the only thing that decides how a food is drawn
// ---------------------------------------------------------------------------

/**
 * Declared, never inferred from an id string. (The venue crew learned this the
 * hard way: guessing from `id.includes('cone')` served ice cream in a
 * restaurant.) A new picture is a case in `src/cooking/pics.ts`.
 */
export type FoodPic =
  | 'fruit'
  | 'berry'
  | 'veg'
  | 'grain'
  | 'milk'
  | 'honey'
  | 'bowl'
  | 'stew'
  | 'cake'
  | 'pancake'
  | 'jam'
  | 'juice'

export interface IngredientDef {
  readonly id: string
  /** DEV ONLY. Never shown to a kid (LAW #4). */
  readonly devName: string
  readonly pic: FoodPic
  readonly color: PaletteName
  readonly accent: PaletteName
  /**
   * Where it usually comes from. A HINT for the "go and find some" picture, and
   * never a restriction — a gift, a catch or a neighbour's basket all count.
   */
  readonly from?: 'garden' | 'pantry' | 'shop' | 'gift'
  /**
   * THE PICTURE OF THE PLACE, when "garden" is too vague to be a picture.
   *
   * `from` is a category; this is what a kid actually walks up to. Fruit comes
   * off the TREE, vegetables out of the BED, berries off the BUSH — three
   * different things standing in the same garden, and a 3-year-old sent to "the
   * garden" for berries has been told nothing. Optional and additive: leave it
   * out and `sourcePicOf()` derives a sensible one from `from`.
   */
  readonly source?: SourcePic
}

/** One line of a recipe: this many of this thing, at this point in the order. */
export interface RecipeStep {
  readonly item: string
  readonly count: number
}

export interface RecipeDef {
  readonly id: string
  /** DEV ONLY. Never shown (LAW #4). */
  readonly devName: string
  /** What comes out, as an item id the bag can hold. */
  readonly dish: string
  readonly pic: FoodPic
  readonly color: PaletteName
  readonly accent: PaletteName
  /** IN ORDER. Non-empty by type — a recipe with nothing in it will not compile. */
  readonly steps: readonly [RecipeStep, ...RecipeStep[]]
  /** How many times the pot is stirred. Pure joy; never a difficulty knob. */
  readonly stirs?: number
  /** Coins for cooking it the first time. Always a gain. */
  readonly coins?: number
  /** Which need eating it answers. Breakfast is `hungry`. */
  readonly feeds?: 'hungry'
}

export function defineIngredient(d: IngredientDef): IngredientDef {
  return d
}
export function defineRecipe<R extends RecipeDef>(r: R): R {
  return r
}

// ---------------------------------------------------------------------------
// registries
// ---------------------------------------------------------------------------

const INGREDIENTS = new Map<string, IngredientDef>()
const RECIPES = new Map<string, RecipeDef>()

export function registerIngredients(list: readonly IngredientDef[]): void {
  for (const d of list) INGREDIENTS.set(d.id, d)
}
export function registerRecipes(list: readonly RecipeDef[]): void {
  for (const r of list) RECIPES.set(r.id, r)
}

export function getIngredient(id: string): IngredientDef | undefined {
  return INGREDIENTS.get(id)
}
export function allIngredients(): IngredientDef[] {
  return [...INGREDIENTS.values()]
}
export function getRecipe(id: string): RecipeDef | undefined {
  return RECIPES.get(id)
}
export function allRecipes(): RecipeDef[] {
  return [...RECIPES.values()]
}

/** Every dish item id the game knows how to cook. Used by the zoo and the bag. */
export function isDish(itemId: string): boolean {
  return allRecipes().some((r) => r.dish === itemId)
}

/** The recipe that makes this dish, if any. */
export function recipeForDish(itemId: string): RecipeDef | undefined {
  return allRecipes().find((r) => r.dish === itemId)
}

/**
 * How a food should be DRAWN, whether it is an ingredient or a dish. One lookup
 * for both, so nothing downstream ever has to know which it is holding.
 */
export interface FoodLook {
  readonly pic: FoodPic
  readonly color: PaletteName
  readonly accent: PaletteName
}

const FALLBACK: FoodLook = { pic: 'bowl', color: 'plate', accent: 'iceMint' }

export function lookOf(itemId: string): FoodLook {
  const ing = INGREDIENTS.get(itemId)
  if (ing !== undefined) return { pic: ing.pic, color: ing.color, accent: ing.accent }
  const dish = recipeForDish(itemId)
  if (dish !== undefined) return { pic: dish.pic, color: dish.color, accent: dish.accent }
  return FALLBACK
}

/**
 * WHERE TO GO AND GET ONE, as a picture key. Total: an unknown item is still
 * answered (the garden bed), because "I do not know" is not a thing this screen
 * may ever say to a kid who is stuck.
 */
export function sourcePicOf(itemId: string): SourcePic {
  const ing = INGREDIENTS.get(itemId)
  if (ing?.source !== undefined) return ing.source
  switch (ing?.from) {
    case 'pantry':
      return 'pantry'
    case 'shop':
      return 'shop'
    case 'gift':
      return 'gift'
    default:
      return 'patch'
  }
}

/** Total of everything a recipe wants — for the "can I make this?" dots. */
export function recipeNeeds(r: RecipeDef): Record<string, number> {
  const out: Record<string, number> = {}
  for (const s of r.steps) out[s.item] = (out[s.item] ?? 0) + Math.max(1, Math.round(s.count))
  return out
}
